import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2 } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { customerService, CustomerDTO } from '../../../../services/customerService';
import '../../../../pages/Products/ProductCategoryDeletePage.css';

import { getServerErrorMessage } from '../../../../utils/errorUtils';
const CustomerDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCustomer = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomerById(id || '0');
      if (response?.data?.data) {
        setCustomer(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('customer.failedToLoadCustomer'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!customer) return;
    setIsDeleting(true);
    try {
      await customerService.deleteCustomer(customer.id);
      navigate('/customers', { state: { message: t('customer.customerDeletedSuccess') } });
    } catch (err: any) {
      setError(getServerErrorMessage(err) || t('customer.failedToDeleteCustomer'));
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('customer.customerNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={() => navigate(`/customers/${id}`)}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('customer.backToCustomer')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('customer.deleteConfirmationTitle')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('customer.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('customer.customerCode')}</label>
                  <p className="delete-info-value delete-code">{customer.code}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('customer.customerName')}</label>
                  <p className="delete-info-value">{customer.name}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('customer.contactEmail')}</label>
                  <p className="delete-info-value">{customer.contactEmail}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('customer.status')}</label>
                  <p className="delete-info-value">
                    <span className={`badge ${customer.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {customer.isActive ? t('customer.active') : t('customer.inactive')}
                    </span>
                  </p>
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate(`/customers/${id}`)}
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('customer.deleteItem')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('customer.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('customer.permanentAction')}</strong>
                    <p>{t('customer.deleteWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('customer.relatedData')}</strong>
                    <p>{t('customer.deleteRelatedDataWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('customer.confirmationRequired')}</strong>
                    <p>{t('customer.deleteConfirmationRequired')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerDeletePage;
